<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue'
import DialogViewHeader from './DialogViewHeader.vue'
import ButtonBase from '@/components/base/ButtonBase.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const props = defineProps({
  title: { type: String, default: '' },
  width: { type: String, default: '500px' },
  height: { type: String, default: 'auto' },
  fullscreen: { type: Boolean, default: false },
  loadingData: { type: Boolean, default: false },
  styleCard: { type: Object, default: () => ({}) },
  classCard: { type: String, default: '' },
  // header
  hasHeader: { type: Boolean, default: true },
  iconHeader: { type: String, default: '' },
  // content
  hasContent: { type: Boolean, default: true },
  loadingDataContent: { type: Boolean, default: false },
  noPaddingContent: { type: Boolean, default: false },
  // actions
  hasActions: { type: Boolean, default: true },
  hasSecondaryBtn: { type: Boolean, default: true },
  handleSecondaryBtn: { type: Function, default: () => {} },
  hasPrimaryBtn: { type: Boolean, default: true },
  textSecondaryBtn: { type: String },
  disabledSecondaryBtn: { type: Boolean, default: false },
  textPrimaryBtn: { type: String },
  colorTextPrimaryBtn: { type: String, default: '' },
  colorPrimaryBtn: { type: String, default: 'var(--sds-color-background-brand-default)' },
  loadingPrimaryBtn: { type: Boolean, default: false },
  disabledPrimaryBtn: { type: Boolean, default: false },
  handleSubmit: { type: Function, default: () => {} },
  zIndex: { type: Number, default: 9999 },
  closeAfterSubmit: { type: Boolean, default: true },
})
const textPrimaryBtn = computed(() => props.textPrimaryBtn ?? t('defaultDialog.save'))
const textSecondaryBtn = computed(() => props.textSecondaryBtn ?? t('defaultDialog.cancel'))

const getHeight = computed(() => {
  return props.fullscreen ? '100vh' : props.height
})
const getWidth = computed(() => {
  return props.fullscreen ? '100vw' : props.width
})
const getStyleBody = computed(() => {
  if (props.noPaddingContent) {
    return {
      paddingTop: '0',
      paddingBottom: '0',
    }
  }
  return {
    paddingTop: props.hasHeader ? '6px' : '24px',
    paddingBottom: props.hasActions ? '0' : '24px',
  }
})

const dialog = ref(false)
const valid = ref(true)
const openDialog = () => {
  dialog.value = true
}
const closeDialog = () => {
  dialog.value = false
}

const handleSecondaryBtn = async () => {
  await props.handleSecondaryBtn()
  closeDialog()
}

const formData = useTemplateRef('formData')
const submitForm = async () => {
  await formData.value?.validate()
  if (!valid.value) return
  await props.handleSubmit()
  if (props.closeAfterSubmit) {
    closeDialog()
  }
}

defineExpose({
  openDialog,
  closeDialog,
  isOpen: dialog,
})
</script>

<template>
  <v-dialog
    v-model="dialog"
    :width="getWidth"
    :height="getHeight"
    :fullscreen="fullscreen"
    v-bind="$attrs"
    :z-index="props.zIndex"
  >
    <v-form
      ref="formData"
      v-model="valid"
      @submit.prevent="submitForm"
      style="width: 100%; height: 100%"
    >
      <v-card :style="styleCard" :class="classCard">
        <v-overlay
          persistent
          :model-value="loadingData"
          class="align-center justify-center"
          contained
        >
          <v-progress-circular indeterminate size="44"></v-progress-circular>
        </v-overlay>
        <v-card-title v-if="hasHeader" class="tw:flex tw:justify-center tw:items-center tw:p-24">
          <slot name="header">
            <DialogViewHeader :title="title" :icon="iconHeader" @close="closeDialog">
              <template #title>
                <slot name="title"></slot>
              </template>
            </DialogViewHeader>
          </slot>
        </v-card-title>
        <v-card-text v-if="hasContent" :style="getStyleBody">
          <v-progress-linear
            v-if="loadingDataContent"
            indeterminate
            height="4"
            color="primary"
            absolute
            bottom
          />
          <slot name="content" />
        </v-card-text>
        <v-card-actions
          v-if="hasActions"
          class="tw:px-24 tw:pb-32 tw:flex tw:items-center tw:gap-16"
        >
          <slot name="actions">
            <ButtonBase
              v-if="hasSecondaryBtn"
              :title="textSecondaryBtn"
              @click="handleSecondaryBtn"
              variant="secondary"
              class="tw:normal-case tw:w-full"
            />
            <ButtonBase
              v-if="hasPrimaryBtn"
              type="submit"
              :loading="loadingPrimaryBtn"
              :title="textPrimaryBtn"
              :activeBgColor="colorPrimaryBtn"
              :text-color="colorTextPrimaryBtn"
              :disabled="disabledPrimaryBtn"
              class="tw:normal-case tw:w-full"
            />
          </slot>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<style scoped>
.v-card {
  overflow: hidden;
}
.v-card-text {
  overflow-y: auto;
}
</style>
